import { useEffect, useState } from 'react';

/**
 * Custom hook to show a warning modal when user tries to leave the page
 * during an ongoing process (like AI generation)
 *
 * @param {boolean} isProcessing - Whether a process is currently running
 * @param {string} warningMessage - Custom warning message
 * @param {string} taskType - Type of task ('ai-chat', 'content-generation', 'image-generation', 'default')
 * @returns {Object} - { showModal, setShowModal, handleConfirmLeave, handleCancelLeave }
 */
const useNavigationWarning = (isProcessing, warningMessage, taskType = 'default') => {
  const [showModal, setShowModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // Handle browser refresh/close
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isProcessing) {
        e.preventDefault();
        e.returnValue = ''; // Modern browsers require this
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isProcessing]);

  // Handle in-app navigation (React Router)
  useEffect(() => {
    if (!isProcessing) return;

    // Store original pushState and replaceState
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    // Intercept pushState
    window.history.pushState = function(...args) {
      if (isProcessing) {
        setShowModal(true);
        setPendingNavigation(() => () => originalPushState.apply(window.history, args));
        return;
      }
      return originalPushState.apply(window.history, args);
    };

    // Intercept replaceState
    window.history.replaceState = function(...args) {
      if (isProcessing) {
        setShowModal(true);
        setPendingNavigation(() => () => originalReplaceState.apply(window.history, args));
        return;
      }
      return originalReplaceState.apply(window.history, args);
    };

    // Handle back/forward buttons
    const handlePopState = (e) => {
      if (isProcessing) {
        e.preventDefault();
        window.history.pushState(null, '', window.location.href);
        setShowModal(true);
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isProcessing]);

  const handleConfirmLeave = () => {
    setShowModal(false);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
  };

  const handleCancelLeave = () => {
    setShowModal(false);
    setPendingNavigation(null);
  };

  return {
    showModal,
    setShowModal,
    handleConfirmLeave,
    handleCancelLeave,
    warningMessage,
    taskType
  };
};

export default useNavigationWarning;
